import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, ShoppingBag, Package, AlertTriangle, TrendingUp, 
  Users, Clock, ArrowRight, Search, Filter, 
  Plus, Minus, Save, RefreshCw, Edit3, Download, Building,
  Calendar, History, RotateCcw, X, Loader, FileText, FileSpreadsheet,
  ChevronDown, CheckCircle2, AlertCircle
} from 'lucide-react';
import { Product, User } from '../types';

interface AdminDashboardProps {
  onNavigate: (page: string) => void;
  user?: User | null;
}

interface DashboardStats {
  total_revenue: number;
  total_orders: number;
  total_products: number;
  pending_orders: number;
  low_stock_items: number;
  total_members: number;
  recent_orders: any[];
  low_stock_products: any[];
  timestamp?: string;
}

interface StatsHistory {
  id: number;
  stat_date: string;
  period_type: string;
  total_revenue: number;
  total_orders: number;
  total_products: number;
  pending_orders: number;
  low_stock_items: number;
  total_members: number;
  snapshot: any;
}

interface ResetHistory {
  id: number;
  reset_date: string;
  reset_by: string;
  period_start: string;
  period_end: string;
  snapshot: any;
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigate, user }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard stats from API
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Store product names for orders
  const [orderProductNames, setOrderProductNames] = useState<Record<number, string>>({});
  const [loadingProductNames, setLoadingProductNames] = useState<Record<number, boolean>>({});
  
  // Stock management
  const [editingStock, setEditingStock] = useState<number | null>(null);
  const [stockValue, setStockValue] = useState<number>(0);
  const [originalStock, setOriginalStock] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<'current' | 'custom'>('current');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [exporting, setExporting] = useState(false);
  
  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [statsHistory, setStatsHistory] = useState<StatsHistory[]>([]);
  const [resetHistory, setResetHistory] = useState<ResetHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState<string>('all');
  
  // Reset confirmation state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetPeriodStart, setResetPeriodStart] = useState('');
  const [resetPeriodEnd, setResetPeriodEnd] = useState('');
  const [resetReason, setResetReason] = useState('');
  
  // API status
  const [apiStatus, setApiStatus] = useState({
    products: false,
    orders: false,
    members: false,
    dashboard: false
  });
  
  const [diagnostics, setDiagnostics] = useState<string[]>([]);

  const addDiagnostic = (msg: string) => {
    console.log(msg);
    setDiagnostics(prev => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`].slice(-5));
  };

  // Helper functions
  const getCustomerName = (order: any): string => {
    return order.customer_name || order.customerName || order.name || order.full_name || 'Guest';
  };

  const getOrderTotal = (order: any): number => {
    return order.total_amount || order.totalAmount || order.total || 0;
  };

  const getOrderDate = (order: any): string => {
    return order.order_date || order.orderDate || order.date || '';
  };

  // Fetch product names for a specific order from the orders API
  const fetchProductNamesForOrder = async (orderId: number): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/get.php?id=${orderId}`);
      const data = await response.json();
      
      if (data.success && data.order) {
        const orderData = data.order;
        
        // Check items array
        if (orderData.items && Array.isArray(orderData.items) && orderData.items.length > 0) {
          return orderData.items.map((item: any) => item.name || item.product_name).join(', ');
        }
        // Check order_items array
        if (orderData.order_items && Array.isArray(orderData.order_items) && orderData.order_items.length > 0) {
          return orderData.order_items.map((item: any) => item.product_name || item.name).join(', ');
        }
        // Single product name
        if (orderData.product_name) {
          return orderData.product_name;
        }
      }
      return `Order #${orderId}`;
    } catch (error) {
      console.error(`Error fetching product names for order ${orderId}:`, error);
      return `Order #${orderId}`;
    }
  };

  // Load product names for recent orders
  const loadProductNamesForOrders = useCallback(async (recentOrdersList: any[]) => {
    if (!recentOrdersList || recentOrdersList.length === 0) return;
    
    for (const order of recentOrdersList) {
      const orderId = order.id;
      
      // Skip if already loaded or currently loading
      if (orderProductNames[orderId] || loadingProductNames[orderId]) continue;
      
      // Check if product names already exist in the order object
      if (order.product_names) {
        setOrderProductNames(prev => ({ ...prev, [orderId]: order.product_names }));
        continue;
      }
      
      if (order.items && order.items.length > 0) {
        const names = order.items.map((item: any) => item.name || item.product_name).join(', ');
        setOrderProductNames(prev => ({ ...prev, [orderId]: names }));
        continue;
      }
      
      if (order.order_items && order.order_items.length > 0) {
        const names = order.order_items.map((item: any) => item.product_name || item.name).join(', ');
        setOrderProductNames(prev => ({ ...prev, [orderId]: names }));
        continue;
      }
      
      if (order.product_name) {
        setOrderProductNames(prev => ({ ...prev, [orderId]: order.product_name }));
        continue;
      }
      
      // Fetch from API
      setLoadingProductNames(prev => ({ ...prev, [orderId]: true }));
      
      const productNames = await fetchProductNamesForOrder(orderId);
      setOrderProductNames(prev => ({ ...prev, [orderId]: productNames }));
      setLoadingProductNames(prev => ({ ...prev, [orderId]: false }));
    }
  }, [orderProductNames, loadingProductNames]);

  // Load product names when recent orders change
  useEffect(() => {
    if (dashboardStats?.recent_orders && dashboardStats.recent_orders.length > 0) {
      loadProductNamesForOrders(dashboardStats.recent_orders);
    }
  }, [dashboardStats?.recent_orders, loadProductNamesForOrders]);

  // FIXED: Updated getDisplayStatus to handle all statuses properly
  const getDisplayStatus = (status: any): string => {
    if (!status) return 'pending';
    
    const statusStr = String(status).toLowerCase();
    
    // Map all possible statuses
    if (statusStr === 'pending') return 'pending';
    if (statusStr === 'paid') return 'paid';
    if (statusStr === 'processing') return 'processing';
    if (statusStr === 'preparing') return 'preparing';
    if (statusStr === 'shipped') return 'shipped';
    if (statusStr === 'delivered') return 'delivered';
    if (statusStr === 'rejected') return 'rejected';
    if (statusStr === 'cancelled') return 'cancelled';
    if (statusStr === 'refunded') return 'refunded';
    if (statusStr === 'returned') return 'returned';
    if (statusStr === 'picked up') return 'picked_up';
    if (statusStr === 'ready for pickup') return 'ready_for_pickup';
    
    // If no match, return the actual status (don't default to pending)
    return statusStr;
  };

  // Get status badge color
  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'picked up':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'processing':
      case 'preparing':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'paid':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-stone-100 text-stone-500 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-500 border-stone-200';
    }
  };

  // Fetch dashboard stats from API
  const fetchDashboardStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/index.php?action=current&_=${Date.now()}`);
      const data = await response.json();
      if (data.success) {
        setDashboardStats(data.data);
        setApiStatus(prev => ({ ...prev, dashboard: true }));
        addDiagnostic(`Dashboard stats loaded: Revenue ₱${data.data.total_revenue}, Orders ${data.data.total_orders}`);
      } else {
        console.error('Failed to fetch dashboard stats:', data.error);
        setApiStatus(prev => ({ ...prev, dashboard: false }));
        calculateLocalStats();
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setApiStatus(prev => ({ ...prev, dashboard: false }));
      calculateLocalStats();
    } finally {
      setStatsLoading(false);
    }
  }, [products, orders, members]);

  const calculateLocalStats = () => {
    const totalRevenue = orders.reduce((sum, order) => sum + (order.total || order.total_amount || 0), 0);
    const totalOrders = orders.length;
    const totalProducts = products.length;
    const pendingOrders = orders.filter(o => o.status === 'Pending' || o.status === 'pending').length;
    const lowStockItems = products.filter(p => (p.stock || 0) < 5).length;
    const totalMembers = members.length;
    
    setDashboardStats({
      total_revenue: totalRevenue,
      total_orders: totalOrders,
      total_products: totalProducts,
      pending_orders: pendingOrders,
      low_stock_items: lowStockItems,
      total_members: totalMembers,
      recent_orders: orders.slice(0, 5),
      low_stock_products: products.filter(p => (p.stock || 0) < 5).slice(0, 10),
      timestamp: new Date().toISOString()
    });
  };

  // Fetch products, orders, members
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/index.php?_=${Date.now()}`);
      const data = await response.json();
      const extracted = extractData(data);
      setProducts(extracted as Product[]);
      setApiStatus(prev => ({ ...prev, products: true }));
      addDiagnostic(`Loaded ${extracted.length} products`);
    } catch (err) {
      addDiagnostic(`Error fetching products: ${err}`);
      setApiStatus(prev => ({ ...prev, products: false }));
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/get.php?admin=true&_=${Date.now()}`);
      const data = await response.json();
      if (data.success && data.orders) {
        setOrders(data.orders);
        setApiStatus(prev => ({ ...prev, orders: true }));
        addDiagnostic(`Loaded ${data.orders.length} orders`);
      } else {
        setApiStatus(prev => ({ ...prev, orders: false }));
      }
    } catch (err) {
      addDiagnostic(`Error fetching orders: ${err}`);
      setApiStatus(prev => ({ ...prev, orders: false }));
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/members/index.php?_=${Date.now()}`);
      const data = await response.json();
      const extracted = extractData(data);
      setMembers(extracted as User[]);
      setApiStatus(prev => ({ ...prev, members: true }));
      addDiagnostic(`Loaded ${extracted.length} members`);
    } catch (err) {
      addDiagnostic(`Error fetching members: ${err}`);
      setApiStatus(prev => ({ ...prev, members: false }));
    }
  };

  const extractData = (response: any): any[] => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.members && Array.isArray(response.members)) return response.members;
    if (response.users && Array.isArray(response.users)) return response.users;
    if (response.products && Array.isArray(response.products)) return response.products;
    return [];
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    addDiagnostic('Starting dashboard data fetch...');
    
    await Promise.all([
      fetchProducts(),
      fetchOrders(),
      fetchMembers()
    ]);
    
    await fetchDashboardStats();
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Capture daily stats automatically
  useEffect(() => {
    if (dashboardStats && !statsLoading) {
      const captureDailyStats = async () => {
        try {
          await fetch(`${API_BASE_URL}/dashboard/index.php?action=capture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ period_type: 'daily' })
          });
        } catch (error) {
          console.error('Error capturing daily stats:', error);
        }
      };
      captureDailyStats();
    }
  }, [dashboardStats, statsLoading]);

  // Fetch history data
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      let url = `${API_BASE_URL}/dashboard/index.php?action=history&limit=100`;
      if (historyPeriod !== 'all') {
        url += `&period=${historyPeriod}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setStatsHistory(data.data?.stats_history || []);
        setResetHistory(data.data?.reset_history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Reset dashboard
  const handleResetDashboard = async () => {
    if (!resetPeriodStart) {
      alert('Please select the period start date for this reset.');
      return;
    }
    
    setResetting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/index.php?action=reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reset_by: user?.name || 'admin',
          period_start: resetPeriodStart,
          period_end: resetPeriodEnd || new Date().toISOString().split('T')[0],
          reason: resetReason || 'Manual reset'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Dashboard reset successfully! Stats have been archived.');
        setShowResetConfirm(false);
        setResetPeriodStart('');
        setResetPeriodEnd('');
        setResetReason('');
        await fetchDashboardStats();
      } else {
        alert('Failed to reset dashboard: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error resetting dashboard:', error);
      alert('Failed to reset dashboard. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  // Export data
  const handleExport = async () => {
    setExporting(true);
    
    let url = `${API_BASE_URL}/dashboard/index.php?action=export&format=${exportFormat}&_=${Date.now()}`;
    
    if (exportPeriod === 'custom' && exportStartDate && exportEndDate) {
      url += `&period=custom&start_date=${exportStartDate}&end_date=${exportEndDate}`;
    } else {
      url += `&period=current`;
    }
    
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      let filename = `dashboard_export_${new Date().toISOString().split('T')[0]}`;
      if (exportPeriod === 'custom' && exportStartDate && exportEndDate) {
        filename = `dashboard_export_${exportStartDate}_to_${exportEndDate}`;
      }
      filename += exportFormat === 'csv' ? '.csv' : '.json';
      
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(urlObj);
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Stock management
  const handleStockEdit = (productId: number, currentStock: number = 0) => {
    setEditingStock(productId);
    setStockValue(currentStock);
    setOriginalStock(currentStock);
  };

  const handleStockChange = (delta: number) => {
    setStockValue(prev => Math.max(0, prev + delta));
  };

  const handleStockSave = async (productId: number) => {
    try {
      const quantityDiff = stockValue - originalStock;
      if (quantityDiff === 0) {
        setEditingStock(null);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/products/update-stock.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          product_id: productId, 
          quantity: Math.abs(quantityDiff),
          operation: quantityDiff > 0 ? 'increase' : 'decrease'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setProducts(prev => prev.map(p => 
          p.id === productId ? { ...p, stock: stockValue } : p
        ));
        setEditingStock(null);
        await fetchDashboardStats();
      } else {
        alert('Failed to update stock: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Network error while updating stock.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStockStatus = (stock: number = 0) => {
    if (stock === 0) return { status: 'Out of Stock', color: 'bg-red-500', textColor: 'text-red-600' };
    if (stock < 5) return { status: 'Low Stock', color: 'bg-orange-500', textColor: 'text-orange-600' };
    return { status: 'In Stock', color: 'bg-green-500', textColor: 'text-green-600' };
  };

  const categories = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const recentOrders = dashboardStats?.recent_orders?.slice(0, 5) || [];

  if (loading) {
    return (
      <div className="pt-44 pb-12 min-h-screen bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <Loader size={40} className="animate-spin text-gold-600 mx-auto mb-4" />
              <p className="text-stone-500">Loading dashboard data...</p>
              <p className="text-xs text-stone-400 mt-2">
                {!apiStatus.products && 'Loading products... '}
                {!apiStatus.orders && 'Loading orders... '}
                {!apiStatus.members && 'Loading members... '}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="font-serif text-3xl text-stone-900">Maison Dashboard</h1>
            <p className="text-stone-500 text-sm font-light mt-1">
              Welcome back, {user?.name || 'Administrator'}. System is ready for new transactions.
            </p>
            <div className="flex gap-2 mt-2 text-xs text-stone-400">
              <span>Products: {dashboardStats?.total_products || 0}</span>
              <span>Orders: {dashboardStats?.total_orders || 0}</span>
              <span>Members: {dashboardStats?.total_members || 0}</span>
              {dashboardStats?.timestamp && (
                <span>Last updated: {new Date(dashboardStats.timestamp).toLocaleTimeString()}</span>
              )}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button 
              onClick={() => setShowHistoryModal(true)}
              className="px-4 py-2 bg-white border border-stone-200 text-stone-600 text-xs uppercase tracking-wider hover:bg-stone-50 transition-colors flex items-center gap-2"
            >
              <History size={14} /> History
            </button>
            <button 
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-wider hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg"
            >
              <Download size={14} /> Export
            </button>
            <button 
              onClick={() => setShowResetConfirm(true)}
              className="px-4 py-2 border border-orange-300 text-orange-600 text-xs uppercase tracking-wider hover:bg-orange-50 transition-colors flex items-center gap-2"
            >
              <RotateCcw size={14} /> Reset
            </button>
            <button 
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-white border border-stone-200 text-stone-600 text-xs uppercase tracking-wider hover:bg-stone-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> 
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-full border bg-emerald-50 text-emerald-600 border-emerald-100">
                <DollarSign size={22} />
              </div>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700">
                {dashboardStats?.total_revenue ? '+100%' : '0%'}
              </span>
            </div>
            <h3 className="text-stone-500 text-xs uppercase tracking-widest font-bold mb-1">Total Revenue</h3>
            <p className="font-serif text-2xl text-stone-900">{formatCurrency(dashboardStats?.total_revenue || 0)}</p>
          </div>
          
          <div className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-full border bg-blue-50 text-blue-600 border-blue-100">
                <ShoppingBag size={22} />
              </div>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700">
                {dashboardStats?.total_orders ? '+100%' : '0%'}
              </span>
            </div>
            <h3 className="text-stone-500 text-xs uppercase tracking-widest font-bold mb-1">Total Orders</h3>
            <p className="font-serif text-2xl text-stone-900">{dashboardStats?.total_orders || 0}</p>
          </div>
          
          <div className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-full border bg-gold-50 text-gold-600 border-gold-100">
                <Package size={22} />
              </div>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-50 text-orange-700">
                {dashboardStats?.low_stock_items || 0} low stock
              </span>
            </div>
            <h3 className="text-stone-500 text-xs uppercase tracking-widest font-bold mb-1">Products in Vault</h3>
            <p className="font-serif text-2xl text-stone-900">{dashboardStats?.total_products || 0}</p>
          </div>
          
          <div className="bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 rounded-full border bg-orange-50 text-orange-600 border-orange-100">
                <Clock size={22} />
              </div>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700">
                {dashboardStats?.pending_orders === 0 ? 'None' : `${dashboardStats?.pending_orders} awaiting`}
              </span>
            </div>
            <h3 className="text-stone-500 text-xs uppercase tracking-widest font-bold mb-1">Pending Orders</h3>
            <p className="font-serif text-2xl text-stone-900">{dashboardStats?.pending_orders || 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Recent Orders - Shows Product Name */}
          <div className="lg:col-span-2 bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
            <div className="p-6 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-serif text-xl text-stone-900">Recent Orders</h3>
              <button 
                onClick={() => onNavigate('admin-transactions')}
                className="text-xs text-gold-600 hover:text-gold-500 uppercase tracking-widest font-bold"
              >
                View All ({dashboardStats?.total_orders || 0})
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-stone-50 text-stone-500 text-[10px] uppercase tracking-widest font-bold">
                  <tr>
                    <th className="px-6 py-4">Product Name</th>
                    <th className="px-6 py-4">Customer</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {recentOrders.length > 0 ? (
                    recentOrders.map((order: any) => {
                      const customerName = getCustomerName(order);
                      const initial = customerName.charAt(0).toUpperCase();
                      const total = getOrderTotal(order);
                      const actualStatus = order.status || 'pending';
                      const statusBadgeClass = getStatusBadgeClass(actualStatus);
                      const displayStatus = getDisplayStatus(actualStatus);
                      
                      // Get product name from state or show loading/fallback
                      let productNames = orderProductNames[order.id];
                      const isLoading = loadingProductNames[order.id];
                      
                      if (!productNames && !isLoading) {
                        productNames = `Order #${order.order_number || order.id}`;
                      }
                      
                      // Truncate long product names
                      const truncatedProductNames = productNames && productNames.length > 50 
                        ? productNames.substring(0, 47) + '...' 
                        : productNames;
                      
                      return (
                        <tr key={order.id} className="hover:bg-stone-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-stone-900 max-w-[250px] truncate" title={productNames}>
                              {isLoading ? (
                                <span className="flex items-center gap-2">
                                  <Loader size={12} className="animate-spin text-gold-600" />
                                  <span className="text-stone-400">Loading...</span>
                                </span>
                              ) : (
                                truncatedProductNames
                              )}
                            </div>
                           </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-stone-200 rounded-full flex items-center justify-center text-[10px] font-bold">
                                {initial}
                              </div>
                              <span className="text-sm text-stone-700">{customerName}</span>
                            </div>
                           </td>
                          <td className="px-6 py-4 text-sm font-serif text-stone-900">
                            {formatCurrency(total)}
                           </td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-2 py-1 rounded-full text-[10px] uppercase font-bold tracking-wide ${statusBadgeClass}`}>
                              {displayStatus}
                            </span>
                           </td>
                          <td className="px-6 py-4 text-xs text-stone-500">
                            {getOrderDate(order).split(' ')[0]}
                           </td>
                         </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-sm text-stone-400 italic">
                        No orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inventory Status */}
          <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
            <div className="p-6 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-serif text-xl text-stone-900">Stock Levels</h3>
              <button 
                onClick={() => onNavigate('admin-products')}
                className="text-xs text-gold-600 hover:text-gold-500 uppercase tracking-widest font-bold"
              >
                Manage ({dashboardStats?.total_products || 0})
              </button>
            </div>
            
            <div className="p-4 border-b border-stone-100 bg-stone-50/30">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-stone-200 rounded-sm text-sm focus:border-gold-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-stone-400" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border border-stone-200 rounded-sm px-2 py-1 text-xs focus:border-gold-500 focus:outline-none"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-0 max-h-96 overflow-y-auto">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((item) => {
                  const stockStatus = getStockStatus(item.stock);
                  return (
                    <div key={item.id} className="p-4 border-b border-stone-50 hover:bg-stone-50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-stone-900">{item.name}</p>
                          <p className="text-xs text-stone-500">{item.category}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center justify-end gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full ${stockStatus.color}`}></span>
                            <span className={`text-[10px] uppercase font-bold ${stockStatus.textColor}`}>
                              {stockStatus.status}
                            </span>
                          </div>
                          <p className="text-xs text-stone-400">{item.stock || 0} Units</p>
                        </div>
                      </div>
                      
                      {editingStock === item.id ? (
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => handleStockChange(-1)} className="p-1 border border-stone-200 rounded hover:bg-stone-50">
                            <Minus size={12} />
                          </button>
                          <input
                            type="number"
                            value={stockValue}
                            onChange={(e) => setStockValue(parseInt(e.target.value) || 0)}
                            className="w-16 text-center border border-stone-200 rounded-sm py-1 text-sm"
                            min="0"
                          />
                          <button onClick={() => handleStockChange(1)} className="p-1 border border-stone-200 rounded hover:bg-stone-50">
                            <Plus size={12} />
                          </button>
                          <button onClick={() => handleStockSave(item.id)} className="ml-auto bg-stone-900 text-white px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm hover:bg-gold-600 transition-colors flex items-center gap-1">
                            <Save size={10} /> Save
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center mt-2">
                          <div className="w-full bg-stone-100 h-1.5 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${(item.stock || 0) === 0 ? 'bg-red-500' : (item.stock || 0) < 5 ? 'bg-orange-500' : 'bg-gold-600'}`}
                              style={{ width: `${Math.min(100, ((item.stock || 0) / 20) * 100)}%` }}
                            />
                          </div>
                          <button onClick={() => handleStockEdit(item.id, item.stock)} className="ml-3 text-stone-400 hover:text-stone-900">
                            <Edit3 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center text-stone-400 text-sm italic">
                  {products.length === 0 ? 'Inventory is empty.' : 'No products match your search.'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Row */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <button onClick={() => onNavigate('admin-add-product')} className="bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="bg-gold-50 p-2 rounded-full group-hover:bg-gold-600 transition-colors">
                <Plus size={16} className="text-gold-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-sm">Add Product</h3>
                <p className="text-xs text-stone-500">Add new items to inventory</p>
              </div>
            </div>
          </button>

          <button onClick={() => onNavigate('admin-transactions')} className="bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 p-2 rounded-full group-hover:bg-blue-600 transition-colors">
                <ShoppingBag size={16} className="text-blue-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-sm">View Orders</h3>
                <p className="text-xs text-stone-500">{dashboardStats?.pending_orders || 0} pending approval</p>
              </div>
            </div>
          </button>

          <button onClick={() => onNavigate('admin-members')} className="bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-50 p-2 rounded-full group-hover:bg-emerald-600 transition-colors">
                <Users size={16} className="text-emerald-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-sm">Members</h3>
                <p className="text-xs text-stone-500">{dashboardStats?.total_members || 0} registered</p>
              </div>
            </div>
          </button>

          <button onClick={() => onNavigate('admin-bank-details')} className="bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-2 rounded-full group-hover:bg-purple-600 transition-colors">
                <Building size={16} className="text-purple-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-sm">Bank Accounts</h3>
                <p className="text-xs text-stone-500">Manage transfer details</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setShowExportModal(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden">
            <div className="bg-stone-900 text-white p-6">
              <div className="flex justify-between items-center">
                <h3 className="font-serif text-xl">Export Dashboard Data</h3>
                <button onClick={() => setShowExportModal(false)} className="text-stone-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Export Period</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={exportPeriod === 'current'} onChange={() => setExportPeriod('current')} className="accent-gold-600" />
                    <span className="text-sm">Current Stats</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={exportPeriod === 'custom'} onChange={() => setExportPeriod('custom')} className="accent-gold-600" />
                    <span className="text-sm">Custom Range</span>
                  </label>
                </div>
              </div>
              
              {exportPeriod === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Start Date</label>
                    <input type="date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} className="w-full border border-stone-300 p-2 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">End Date</label>
                    <input type="date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} className="w-full border border-stone-300 p-2 rounded-sm text-sm" />
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Export Format</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={exportFormat === 'csv'} onChange={() => setExportFormat('csv')} className="accent-gold-600" />
                    <FileText size={16} /> CSV
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={exportFormat === 'json'} onChange={() => setExportFormat('json')} className="accent-gold-600" />
                    <FileSpreadsheet size={16} /> JSON
                  </label>
                </div>
              </div>
              
              <button onClick={handleExport} disabled={exporting} className="w-full bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {exporting ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                {exporting ? 'Exporting...' : 'Export Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setShowResetConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden">
            <div className="bg-orange-600 text-white p-6">
              <div className="flex justify-between items-center">
                <h3 className="font-serif text-xl">Reset Dashboard</h3>
                <button onClick={() => setShowResetConfirm(false)} className="text-white/80 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <p className="text-xs text-white/80 mt-2">This will archive current data and reset counters. All data will still be viewable in history.</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Period Start Date *</label>
                <input type="date" value={resetPeriodStart} onChange={(e) => setResetPeriodStart(e.target.value)} className="w-full border border-stone-300 p-2 rounded-sm text-sm" required />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Period End Date</label>
                <input type="date" value={resetPeriodEnd} onChange={(e) => setResetPeriodEnd(e.target.value)} className="w-full border border-stone-300 p-2 rounded-sm text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Reason (Optional)</label>
                <input type="text" value={resetReason} onChange={(e) => setResetReason(e.target.value)} placeholder="e.g., Monthly reset, Quarter end, etc." className="w-full border border-stone-300 p-2 rounded-sm text-sm" />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowResetConfirm(false)} className="flex-1 border border-stone-300 text-stone-600 py-2 text-xs uppercase tracking-widest hover:bg-stone-50">Cancel</button>
                <button onClick={handleResetDashboard} disabled={resetting || !resetPeriodStart} className="flex-1 bg-orange-600 text-white py-2 text-xs uppercase tracking-widest hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {resetting ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  {resetting ? 'Resetting...' : 'Confirm Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => setShowHistoryModal(false)}></div>
          <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-sm shadow-2xl mx-4 flex flex-col">
            <div className="bg-stone-900 text-white p-6 flex justify-between items-center">
              <div>
                <h3 className="font-serif text-xl">Dashboard History</h3>
                <p className="text-xs text-stone-400 mt-1">View historical snapshots and reset records</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-stone-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 border-b border-stone-200 bg-stone-50 flex gap-2">
              <button onClick={() => { setHistoryPeriod('all'); fetchHistory(); }} className={`px-3 py-1 text-xs rounded-full ${historyPeriod === 'all' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`}>All</button>
              <button onClick={() => { setHistoryPeriod('daily'); fetchHistory(); }} className={`px-3 py-1 text-xs rounded-full ${historyPeriod === 'daily' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`}>Daily</button>
              <button onClick={() => { setHistoryPeriod('weekly'); fetchHistory(); }} className={`px-3 py-1 text-xs rounded-full ${historyPeriod === 'weekly' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`}>Weekly</button>
              <button onClick={() => { setHistoryPeriod('monthly'); fetchHistory(); }} className={`px-3 py-1 text-xs rounded-full ${historyPeriod === 'monthly' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`}>Monthly</button>
              <button onClick={fetchHistory} className="ml-auto px-3 py-1 text-xs bg-stone-200 text-stone-600 rounded-full hover:bg-stone-300 flex items-center gap-1">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {historyLoading ? (
                <div className="flex justify-center py-12">
                  <Loader size={32} className="animate-spin text-gold-600" />
                </div>
              ) : (
                <div className="space-y-8">
                  {statsHistory.length > 0 && (
                    <div>
                      <h4 className="font-serif text-lg text-stone-900 mb-4 flex items-center gap-2">
                        <Calendar size={18} /> Historical Snapshots
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-stone-100">
                            <tr>
                              <th className="px-4 py-2 text-left">Date</th>
                              <th className="px-4 py-2 text-left">Period</th>
                              <th className="px-4 py-2 text-right">Revenue</th>
                              <th className="px-4 py-2 text-right">Orders</th>
                              <th className="px-4 py-2 text-right">Products</th>
                              <th className="px-4 py-2 text-right">Members</th>
                             </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {statsHistory.map((stat) => (
                              <tr key={stat.id} className="hover:bg-stone-50">
                                <td className="px-4 py-3">{stat.stat_date}</td>
                                <td className="px-4 py-3 capitalize">{stat.period_type}</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(stat.total_revenue)}</td>
                                <td className="px-4 py-3 text-right">{stat.total_orders}</td>
                                <td className="px-4 py-3 text-right">{stat.total_products}</td>
                                <td className="px-4 py-3 text-right">{stat.total_members}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {resetHistory.length > 0 && (
                    <div>
                      <h4 className="font-serif text-lg text-stone-900 mb-4 flex items-center gap-2">
                        <RotateCcw size={18} /> Reset History
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-stone-100">
                            <tr>
                              <th className="px-4 py-2 text-left">Reset Date</th>
                              <th className="px-4 py-2 text-left">Reset By</th>
                              <th className="px-4 py-2 text-left">Period</th>
                              <th className="px-4 py-2 text-right">Revenue at Reset</th>
                              <th className="px-4 py-2 text-right">Orders at Reset</th>
                             </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100">
                            {resetHistory.map((reset) => (
                              <tr key={reset.id} className="hover:bg-stone-50">
                                <td className="px-4 py-3">{new Date(reset.reset_date).toLocaleDateString()}</td>
                                <td className="px-4 py-3">{reset.reset_by}</td>
                                <td className="px-4 py-3">{reset.period_start} - {reset.period_end}</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(reset.snapshot?.current_stats?.total_revenue || 0)}</td>
                                <td className="px-4 py-3 text-right">{reset.snapshot?.current_stats?.total_orders || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {statsHistory.length === 0 && resetHistory.length === 0 && (
                    <div className="text-center py-12 text-stone-400">
                      <History size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No history data available yet.</p>
                      <p className="text-xs mt-2">Data will appear here after dashboard resets or daily captures.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;